import { useParams } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import ArticleCard from '@/components/ArticleCard';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory, articles as allArticles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900">分类未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该分类不存在或已被移除</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = getArticlesByCategory(category.id);
  const otherCategories = categories.filter((c) => c.id !== category.id);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name} - 猎奇吃瓜`,
    description: category.description,
    url: `http://odki7b.com/category/${category.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://odki7b.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `http://odki7b.com/category/${category.slug}`,
      },
    ],
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: categoryArticles.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `http://odki7b.com/article/${article.id}`,
      name: article.title,
    })),
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 猎奇吃瓜 | 每日新鲜吃瓜八卦`}
        description={category.description}
        keywords={`${category.name},猎奇吃瓜,新鲜吃瓜八卦,冷门趣味爆料,无压力围观趣事,新奇八卦合集,猎奇向娱乐梗,解压谈资`}
        canonical={`http://odki7b.com/category/${category.slug}`}
        ogImage={category.image}
        schema={[pageSchema, breadcrumbSchema, itemListSchema]}
      />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
          </div>

          <div className="relative w-full px-4 md:px-6 lg:px-10 pb-10 md:pb-14 pt-24 md:pt-32">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {category.name}
            </h1>
            <p className="mt-2 md:mt-3 text-sm md:text-base text-white/80 max-w-xl">
              {category.description}
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="bg-background-50 py-10 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-10">
            {/* Category Switcher Tabs */}
            <div className="flex flex-wrap gap-2 mb-6 md:mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.id === category.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-300" />
                <p className="mt-3 text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="bg-background-100 py-10 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              探索其他分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {otherCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative rounded-xl overflow-hidden aspect-[4/3] block"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <span className="text-white font-semibold text-sm">{cat.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-14 md:py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20with%20soft%20glow%20particles%2C%20coral%20and%20amber%20tones%2C%20energetic%20and%20inviting%20atmosphere%2C%20modern%20digital%20art%2C%20subtle%20bokeh%20effect&width=1600&height=600&seq=cta-cat&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50" />
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
              订阅{category.name}更新
            </h2>
            <p className="mt-2 text-sm text-white/70">
              不错过任何一条{category.name}相关的最新爆料
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <a
                href="mailto:hello@odki7b.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-foreground-950 rounded-full text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                立即订阅
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}